import React, { useEffect, useState } from 'react';
import useRoute from '@/Hooks/useRoute';
import ApplicationMark from '@/Components/ApplicationMark';
import NavLink from '@/Components/NavLink';
import IconDash from '@/svgs/dashboard.svg?react';
import IconFolder from '@/svgs/folder.svg?react';
import IconProjects from '@/svgs/earth.svg?react';
import IconBook from '@/svgs/book-read.svg?react';
import IconCollapse from '@/svgs/layout-left.svg?react';
import IconMoney from '@/svgs/money-dollar-circle.svg?react';
import IconCode from '@/svgs/code-box.svg?react';
import IconHelp from '@/svgs/customer-service.svg?react';
import IconConvo from '@/svgs/customer-service.svg?react';
import IconUser from '@/svgs/user-smile.svg?react';
import IconLogout from '@/svgs/download.svg?react';
import IconClose from '@/svgs/close.svg?react';
import IconOpenNew from '@/svgs/open-in-new-tab.svg?react';
import IconSupport from '@/svgs/support-2.svg?react';
import IconSchool from '@/svgs/school.svg?react';
import IconEvent from '@/svgs/event.svg?react';
import IconChat from '@/svgs/chat-bubble.svg?react';
import useTypedPage from '@/Hooks/useTypedPage';
import AlertAccountLimits from '@/Components/AlertAccountLimits';
import ButtonCreateFile from './ButtonCreateFile';
import { useUiSettings } from '@/Contexts/UiSettingsProvider';
import { useIntercom } from 'react-use-intercom';
import Badge from '@/Components/Badge';
import Popover from '@/Components/Popover';
import Logout from '@/Pages/Profile/Partials/Logout';
import Button from '@/Components/Button';
import { cn } from '@/utils';

export default function AppNav() {
  const {
    auth: { user },
    file,
    sunset,
  } = useTypedPage().props;
  const route = useRoute();
  const { show, boot, update } = useIntercom();
  const { settings, setSettings } = useUiSettings();
  const [navCollapsed, setNavCollapsed] = useState<boolean | undefined>(
    settings.navCollapsed
  );
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const page = useTypedPage();
  const team = page.props.auth?.user?.current_team;
  const { proxied } = useTypedPage<{ proxied: boolean }>().props;

  const isSnippetViewVisible = route().current('snippet.show', {
    file: file?.id,
  });

  const supportOptions = [
    {
      title: 'Help center and documentation',
      description:
        'Access a written help center with high-quality ' +
        'documentation and video tutorials.',
      icon: <IconSupport className="mr-2 h-5 w-5" />,
      externalLink: 'https://intercom.help/wpai/en/',
      onClick: () => {},
    },
    {
      title: '#PromptEDU',
      description:
        'A series of video and blog posts teaching ' +
        'how best to prompt CodeWP and other ' +
        'generative AI tools.',
      icon: <IconSchool className="mr-2 h-5 w-5" />,
      externalLink: 'https://codewp.ai/blog/category/prompting',
      onClick: () => {},
    },
    {
      title: 'Talk with the founder',
      description:
        'Video chat with James for 15mins every Friday. ' +
        'Schedule your slot today to leave feedback, ' +
        'ask questions, and more.',
      icon: <IconEvent className="mr-2 h-5 w-5" />,
      externalLink: 'https://calendly.com/jwlp/platform',
      onClick: () => {},
    },
    {
      title: 'Contact Us',
      description:
        'Contact us by creating a ticket and we ' +
        'will get back to your query as soon as ' +
        'humanly possible.',
      icon: <IconChat className="mr-2 h-5 w-5" />,
      externalLink: null,
      onClick: (e: React.FormEvent) => {
        e.preventDefault();
        setIsSupportModalOpen(false);
        launchIntercom();
      },
    },
  ];

  function closeSupportModal() {
    setIsSupportModalOpen(false);
  }

  function handleNavCollapse() {
    setNavCollapsed(!navCollapsed);
    setSettings(prev => ({ ...prev, navCollapsed: !navCollapsed }));
  }

  function launchIntercom() {
    show();
  }

  useEffect(() => {
    update({
      horizontalPadding: (navCollapsed ? 64 : 240) + 20,
    });
  }, [navCollapsed]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 992) {
        setNavCollapsed(true);
      }
    };

    boot({
      name: user?.name,
      email: user?.email,
      createdAt: String(user?.created_at),
      alignment: 'left',
      horizontalPadding: (navCollapsed ? 64 : 240) + 20,
      hideDefaultLauncher: true,
    });

    handleResize();

    // Add event listener for window resize to update navCollapsed state
    window.addEventListener('resize', handleResize);

    return () => {
      // Remove event listener on component cleanup
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      className={cn(
        'h-full overflow-y-auto bg-brand-25 text-lg text-brand-dark-25 transition-all duration-200 ease-in-out dark:bg-brand-dark-75 dark:text-brand-dark-25',
        {
          'w-60': navCollapsed == false,
          'collapsed w-16': navCollapsed == true,
        },
        {
          'fixed z-20 lg:relative': isSnippetViewVisible,
        }
      )}
    >
      <div className="flex h-full flex-col justify-between">
        <div>
          <div
            className={cn('flex justify-between px-3 py-6', {
              'flex-col items-center justify-center': navCollapsed == true,
            })}
          >
            <Button
              styleType="link"
              href={route('dashboard')}
              className="flex gap-2"
              forceUrlDomain="app"
            >
              <ApplicationMark />
              <Badge className="collapsable">{team?.billing.plan}</Badge>
            </Button>

            <button
              onClick={handleNavCollapse}
              className={cn('transition', {
                'mt-6 scale-[-1]': navCollapsed == true,
                'scale-1': navCollapsed == false,
              })}
            >
              <IconCollapse className="text-brand-dark-100/40 dark:text-brand-light-25" />
            </button>
          </div>
          <div className="px-3 py-6" data-tour="3">
            <ButtonCreateFile route={route('files.create')} />
          </div>
          <nav className="flex flex-col gap-3 px-3">
            <NavLink
              href={route('dashboard')}
              active={route().current()?.startsWith('dashboard')}
              hideText={navCollapsed}
              text="Dashboard"
              tooltipSide={navCollapsed ? 'right' : 'top'}
              data-tour="2"
              forceUrlDomain="app"
            >
              <IconDash className="h-6 w-6" />
            </NavLink>
            {proxied && (
              <NavLink
                href={route('snippets.index')}
                active={route().current('snippets.index')}
                forceUrlDomain="website"
              >
                <IconCode className="h-6 w-6" />
                <span className="collapsable">Public Snippets</span>
              </NavLink>
            )}
            <NavLink
              href={route('packages.index')}
              active={route().current('packages.index')}
              hideText={navCollapsed}
              text="Packages"
              tooltipSide={navCollapsed ? 'right' : 'top'}
              forceUrlDomain="app"
            >
              <IconFolder className="h-6 w-6" />
            </NavLink>
            <NavLink
              href={route('sites.index')}
              active={route().current()?.startsWith('projects')}
              hideText={navCollapsed}
              text="Sites"
              tooltipSide={navCollapsed ? 'right' : 'top'}
              forceUrlDomain="app"
            >
              <IconProjects className="h-6 w-6" />
            </NavLink>

            <NavLink
              href={route('conversations.index')}
              active={route().current('conversations.index')}
              hideText={navCollapsed}
              text="Chats"
              tooltipSide={navCollapsed ? 'right' : 'top'}
              forceUrlDomain="app"
            >
              <IconConvo className="h-6 w-6 text-brand-dark-100/40 dark:text-brand-light-25" />
            </NavLink>
          </nav>
        </div>

        <div className="my-12">
          {!sunset && team && team.billing.plan !== 'agency' && (
            <AlertAccountLimits team={team} />
          )}

          <div className="flex flex-1 flex-col space-y-3 px-3">
            <NavLink
              href="https://codewp.ai/affiliates/"
              active={false}
              hideText={navCollapsed}
              as="externalLink"
              text="Affiliate Program"
              tooltipSide={navCollapsed ? 'right' : 'top'}
            >
              <IconMoney className="h-6 w-6 text-brand-dark-100/40 dark:text-brand-light-25" />
            </NavLink>
            <NavLink
              onClick={() => setIsSupportModalOpen(!isSupportModalOpen)}
              hideText={navCollapsed}
              text="Help & Support"
              tooltipSide={navCollapsed ? 'right' : 'top'}
            >
              <IconHelp className="h-6 w-6 text-brand-dark-100/40 dark:text-brand-light-25" />
            </NavLink>
            <Popover
              open={isSupportModalOpen}
              style={{
                width: 350,
                zIndex: 1,
              }}
              side="right"
              sideOffset={25}
              align="end"
              alignOffset={-50}
              closeAction={closeSupportModal}
              content={
                <div className="popover-content-left flex flex-col divide-y divide-gray-800 rounded-xl bg-brand-25 pb-8 text-brand-dark-25 dark:bg-brand-dark-75 dark:text-brand-dark-25">
                  <div className="flex items-center justify-between p-4">
                    <h2 className="text-brand-dark-100/40 dark:text-brand-light-75">
                      Help & Support
                    </h2>

                    <IconClose
                      onClick={() => setIsSupportModalOpen(!isSupportModalOpen)}
                      className="cursor-pointer text-brand-dark-100/40 dark:text-brand-light-25"
                    />
                  </div>

                  <div>
                    {supportOptions.map((option, idx) => (
                      <a
                        href={option.externalLink}
                        target={option.externalLink ? '_blank' : '_self'}
                        key={`key-${idx}`}
                        className="flex cursor-pointer flex-col bg-transparent px-4 py-3 text-brand-dark-100/40 hover:bg-brand-50 dark:text-brand-light-25 hover:dark:bg-brand-dark-50 hover:dark:text-brand-light-100"
                        onClick={option.onClick}
                      >
                        <p className="flex text-brand-dark-100/40 dark:text-brand-light-75">
                          {option.icon}
                          {option.title}
                        </p>
                        <div className="mt-1 flex items-center">
                          <p className="text-brand-dark-100/40 dark:text-brand-light-25">
                            {option.description}
                          </p>
                          <IconOpenNew className="ml-2 w-4 shrink-0 text-brand-dark-100/40 dark:text-brand-light-75" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              }
            />
            <NavLink
              href="https://codewp.ai/terms-of-service"
              active={false}
              hideText={navCollapsed}
              text="Legal & Terms"
              tooltipSide={navCollapsed ? 'right' : 'top'}
            >
              <IconBook className="h-6 w-6 text-brand-dark-100/40 dark:text-brand-light-25" />
            </NavLink>
            <NavLink
              href={route('profile.show')}
              active={false}
              hideText={navCollapsed}
              text="My Account"
              tooltipSide={navCollapsed ? 'right' : 'top'}
              forceUrlDomain="app"
            >
              <IconUser className="h-6 w-6 text-brand-dark-100/40 dark:text-brand-light-25" />
            </NavLink>
            {!proxied && (
              <Logout>
                <NavLink
                  type="submit"
                  active={false}
                  hideText={navCollapsed}
                  text="Logout"
                  className="w-full"
                  tooltipSide={navCollapsed ? 'right' : 'top'}
                >
                  <IconLogout className="h-6 w-6 rotate-90 text-brand-dark-100/40 dark:text-brand-light-25" />
                </NavLink>
              </Logout>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
